import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Card,
    Grid,
    Button,
    Avatar,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
} from '@mui/material';
import { apiUrl } from '../../Config';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';


const formatSearchCriteria = (search) => {
    if (!search) return 'Keyword Search';
    const parts = [];
    
    // Skills
    if (search.skillNames && search.skillNames.length > 0) {
        parts.push(`Skills: ${search.skillNames.join(', ')}`);
    } else if (search.criteria?.skills) {
        parts.push(`Skills: ${search.criteria.skills}`);
    }
    
    // Experience
    if (search.criteria?.experience) {
        parts.push(`Exp: ${search.criteria.experience}`);
    }
    
    // Notice Period
    if (search.criteria?.noticePeriod) {
        parts.push(`Notice: ${search.criteria.noticePeriod}`);
    }
    
    // Gender
    if (search.criteria?.gender) {
        parts.push(`Gender: ${search.criteria.gender}`);
    }
    
    // Education
    if (search.criteria?.education) {
        parts.push(`Edu: ${search.criteria.education}`);
    }
    
    if (parts.length === 0) {
        return 'Keyword Search';
    }
    
    return parts.join(' · ');
};

const formatSearchTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState({});
    const [activeJobs, setActiveJobs] = useState([]);
    const [todayInterviews, setTodayInterviews] = useState([]);
    const [tomorrowInterviews, setTomorrowInterviews] = useState([]);
    const [recentlySearched, setRecentlySearched] = useState([]);
    const [statsCards, setStatsCards] = useState([
        { title: 'Posted Jobs', count: 0, icon: <WorkOutlineIcon />, color: '#6B31C4' },
        { title: 'Applications', count: 0, icon: <DescriptionOutlinedIcon />, color: '#2563EB' },
        { title: 'Shortlisted', count: 0, icon: <CheckCircleOutlineIcon />, color: '#059669' },
        { title: 'Interview', count: 0, icon: <EventNoteOutlinedIcon />, color: '#7C3AED' },
        { title: 'Hired', count: 0, icon: <PersonAddOutlinedIcon />, color: '#0D9488' },
        { title: 'Rejected', count: 0, icon: <CancelOutlinedIcon />, color: '#DC2626' },
    ]);
    const [loading, setLoading] = useState(true);

    const getToken = () => localStorage.getItem('token') || localStorage.getItem('authToken');
    const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [profileRes, activeJobsRes, interviewsRes, recentSearchRes] = await Promise.allSettled([
                    axios.get(`${apiUrl}/employee/profile`, { headers: authHeader() }),
                    axios.get(`${apiUrl}/dashboard/active-jobs`, { headers: authHeader() }),
                    axios.get(`${apiUrl}/dashboard/interviewers-list`, { headers: authHeader() }),
                    axios.get(`${apiUrl}/users/recent-searches`, { headers: authHeader() }),
                ]);

                // Profile
                if (profileRes.status === 'fulfilled') {
                    const p = profileRes.value.data?.data || profileRes.value.data || {};
                    setProfile(p);
                }

                // Active Jobs
                if (activeJobsRes.status === 'fulfilled') {
                    const jobs = activeJobsRes.value.data?.data || activeJobsRes.value.data?.jobs || activeJobsRes.value.data || [];
                    const jobsArr = Array.isArray(jobs) ? jobs : [];
                    setActiveJobs(jobsArr);

                    // Aggregate stats from active jobs
                    const totals = jobsArr.reduce((acc, job) => ({
                        posted: acc.posted + 1,
                        applications: acc.applications + (job.applications || 0),
                        shortlisted: acc.shortlisted + (job.shortlisted || 0),
                        interview: acc.interview + (job.interview || 0),
                        hired: acc.hired + (job.hired || 0),
                        rejected: acc.rejected + (job.rejected || 0),
                    }), { posted: 0, applications: 0, shortlisted: 0, interview: 0, hired: 0, rejected: 0 });

                    setStatsCards(prev => prev.map(card => {
                        switch (card.title) {
                            case 'Posted Jobs': return { ...card, count: totals.posted };
                            case 'Applications': return { ...card, count: totals.applications };
                            case 'Shortlisted': return { ...card, count: totals.shortlisted };
                            case 'Interview': return { ...card, count: totals.interview };
                            case 'Hired': return { ...card, count: totals.hired };
                            case 'Rejected': return { ...card, count: totals.rejected };
                            default: return card;
                        }
                    }));
                }

                // Interviewers List
                if (interviewsRes.status === 'fulfilled') {
                    const interviewData = interviewsRes.value.data?.data || interviewsRes.value.data || {};
                    const todayList = interviewData.today?.interviews || (Array.isArray(interviewData.today) ? interviewData.today : []);
                    const tomorrowList = interviewData.tomorrow?.interviews || (Array.isArray(interviewData.tomorrow) ? interviewData.tomorrow : []);
                    setTodayInterviews(todayList);
                    setTomorrowInterviews(tomorrowList);
                }

                // Recent Searches
                if (recentSearchRes.status === 'fulfilled') {
                    const searches = recentSearchRes.value.data?.data || recentSearchRes.value.data?.searches || recentSearchRes.value.data || [];
                    setRecentlySearched(Array.isArray(searches) ? searches : []);
                }
            } catch (err) {
                console.error('Dashboard fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Interview row component ── */
    const InterviewRow = ({ interview }) => (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            px: 0.5,
            borderRadius: '8px',
            transition: 'background-color 0.15s ease',
            '&:hover': { backgroundColor: '#F8F9FA' },
        }}>
            <Avatar
                src={interview.avatar}
                alt={interview.name}
                sx={{ width: 36, height: 36, fontSize: '0.875rem' }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1A1A2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {interview.name}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {interview.role}
                </Typography>
            </Box>
            <Chip
                label={interview.type}
                size="small"
                sx={{
                    backgroundColor: interview.type === 'Phone' ? '#EEF2FF' : '#FEF2F2',
                    color: interview.type === 'Phone' ? '#4F46E5' : '#DC2626',
                    fontWeight: 500,
                    fontSize: '0.6875rem',
                    height: 22,
                }}
            />
            <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, minWidth: '50px', textAlign: 'right' }}>
                {interview.time}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '1400px', mx: 'auto' }}>

            {/* Welcome */}
            <Box sx={{ mb: 4 }}>
                {loading ? (
                    <Skeleton variant="text" width={280} height={36} />
                ) : (
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Welcome back, {profile?.name || profile?.firstName || 'there'}
                    </Typography>
                )}
                <Typography sx={{ color: '#6B7280', fontSize: '0.9375rem' }}>
                    {loading ? (
                        <Skeleton variant="text" width={300} height={22} />
                    ) : (
                        `You have ${statsCards[1]?.count || 0} new applications today`
                    )}
                </Typography>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {statsCards.map((card, index) => (
                    <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
                        <Card sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            transition: 'border-color 0.2s ease',
                            '&:hover': { borderColor: '#D1D5DB' },
                        }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '8px',
                                backgroundColor: `${card.color}0A`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: card.color,
                                flexShrink: 0,
                                '& .MuiSvgIcon-root': { fontSize: 22 },
                            }}>
                                {card.icon}
                            </Box>
                            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                <Typography sx={{ fontSize: '0.6875rem', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {card.title}
                                </Typography>
                                {loading ? (
                                    <Skeleton variant="text" width={40} height={28} />
                                ) : (
                                    <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, color: '#1A1A2E', lineHeight: 1.2 }}>
                                        {card.count}
                                    </Typography>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Main Grid: Active Jobs + Interviews */}
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' }, mb: 4 }}>

                {/* Active Jobs */}
                <Card sx={{ flex: { lg: 3 }, overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F3F4F6' }}>
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1A1A2E' }}>
                            Active Jobs
                        </Typography>
                        <Button 
                            size="small" 
                            endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={() => navigate('/jobs')}
                            sx={{ fontSize: '0.8125rem', color: '#6B31C4', '&:hover': { backgroundColor: 'rgba(107,49,196,0.04)' } }}
                        >
                            View All
                        </Button>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell align="center">Applied</TableCell>
                                    <TableCell align="center">Shortlisted</TableCell>
                                    <TableCell align="center">Interview</TableCell>
                                    <TableCell align="center">Hired</TableCell>
                                    <TableCell align="center">Rejected</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [1, 2, 3].map((item) => (
                                        <TableRow key={item}>
                                            <TableCell><Skeleton variant="text" height={20} width="70%" /></TableCell>
                                            <TableCell align="center"><Skeleton variant="rounded" height={24} width={40} sx={{ mx: 'auto' }} /></TableCell>
                                            <TableCell align="center"><Skeleton variant="rounded" height={24} width={40} sx={{ mx: 'auto' }} /></TableCell>
                                            <TableCell align="center"><Skeleton variant="rounded" height={24} width={40} sx={{ mx: 'auto' }} /></TableCell>
                                            <TableCell align="center"><Skeleton variant="rounded" height={24} width={40} sx={{ mx: 'auto' }} /></TableCell>
                                            <TableCell align="center"><Skeleton variant="rounded" height={24} width={40} sx={{ mx: 'auto' }} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : activeJobs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#9CA3AF' }}>
                                            No active jobs found
                                        </TableCell>
                                    </TableRow>
                                ) : activeJobs.map((job, index) => (
                                    <TableRow key={job.id || index}>
                                        <TableCell sx={{ fontWeight: 500, color: '#1A1A2E' }}>
                                            {job.title}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                                                {job.applications ?? 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#059669' }}>
                                                {job.shortlisted ?? 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#7C3AED' }}>
                                                {job.interview ?? 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#0D9488' }}>
                                                {String(job.hired ?? 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#DC2626' }}>
                                                {job.rejected ?? 0}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>

                {/* Interviews */}
                <Card sx={{ flex: { lg: 2 } }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1A1A2E' }}>
                            Interviews
                        </Typography>
                        <Button 
                            size="small"
                            endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={() => navigate('/interviews')}
                            sx={{ fontSize: '0.8125rem', color: '#6B31C4', '&:hover': { backgroundColor: 'rgba(107,49,196,0.04)' } }}
                        >
                            View All
                        </Button>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                        {/* Today */}
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF', mb: 1 }}>
                            Today
                        </Typography>
                        {loading ? (
                            [1, 2].map((item) => (
                                <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                                    <Skeleton variant="circular" width={36} height={36} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" width="60%" height={18} />
                                        <Skeleton variant="text" width="40%" height={14} />
                                    </Box>
                                </Box>
                            ))
                        ) : todayInterviews.length === 0 ? (
                            <Typography sx={{ color: '#9CA3AF', py: 2, fontSize: '0.8125rem', textAlign: 'center' }}>
                                No interviews today
                            </Typography>
                        ) : todayInterviews.map((interview, index) => (
                            <InterviewRow key={index} interview={interview} />
                        ))}

                        {/* Tomorrow */}
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9CA3AF', mb: 1, mt: 3 }}>
                            Tomorrow
                        </Typography>
                        {!loading && tomorrowInterviews.length === 0 && (
                            <Typography sx={{ color: '#9CA3AF', py: 2, fontSize: '0.8125rem', textAlign: 'center' }}>
                                No interviews tomorrow
                            </Typography>
                        )}
                        {!loading && tomorrowInterviews.map((interview, index) => (
                            <InterviewRow key={index} interview={interview} />
                        ))}
                    </Box>
                </Card>
            </Box>

            {/* Recently Searched */}
            <Card>
                <Box sx={{ p: 2.5, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1A1A2E' }}>
                        Recent Searches
                    </Typography>
                    <Button 
                        size="small"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => navigate('/search-candidate')}
                        sx={{ fontSize: '0.8125rem', color: '#6B31C4', '&:hover': { backgroundColor: 'rgba(107,49,196,0.04)' } }}
                    >
                        Search
                    </Button>
                </Box>
                <Box sx={{ p: 2.5 }}>
                    {loading ? (
                        [1, 2].map((item) => (
                            <Box key={item} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
                                <Skeleton variant="text" width="25%" height={20} />
                                <Skeleton variant="text" width="20%" height={20} />
                                <Skeleton variant="text" width="35%" height={20} />
                                <Skeleton variant="text" width="10%" height={20} />
                            </Box>
                        ))
                    ) : recentlySearched.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', color: '#9CA3AF', py: 3, fontSize: '0.875rem' }}>
                            No recent searches
                        </Typography>
                    ) : recentlySearched.map((search, index) => (
                        <Box
                            key={search.id || index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3,
                                py: 1.5,
                                borderBottom: index < recentlySearched.length - 1 ? '1px solid #F3F4F6' : 'none',
                                flexWrap: { xs: 'wrap', md: 'nowrap' },
                            }}
                        >
                            <Box sx={{ minWidth: { xs: '100%', sm: '120px' }, maxWidth: { sm: '150px' } }}>
                                <Typography sx={{ fontSize: '0.6875rem', color: '#9CA3AF', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Title
                                </Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: '#6B31C4', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {search.jobTitle || 'Any Job Role'}
                                </Typography>
                            </Box>

                            <Box sx={{ minWidth: { xs: '45%', sm: '120px' }, maxWidth: { sm: '160px' } }}>
                                <Typography sx={{ fontSize: '0.6875rem', color: '#9CA3AF', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Location
                                </Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: '#374151', fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {search.locationNames?.join(', ') || search.criteria?.preferredLocationNames?.join(', ') || 'Any Location'}
                                </Typography>
                            </Box>

                            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '180px' } }}>
                                <Typography sx={{ fontSize: '0.6875rem', color: '#9CA3AF', mb: 0.25, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Criteria
                                </Typography>
                                <Typography sx={{ fontSize: '0.875rem', color: '#374151', fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {formatSearchCriteria(search)}
                                </Typography>
                            </Box>

                            <Box sx={{ minWidth: '70px', textAlign: 'right' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 400 }}>
                                    {formatSearchTime(search.createdAt)}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Card>
        </Box>
    );
};

export default Dashboard;